import React from 'react';
import { observer } from 'mobx-react-lite';
import { debugPanelStore } from '../stores/debugPanelStore';
import { appConfig } from '../config';

const DebugPanel: React.FC = observer(() => {
  if (!debugPanelStore.isVisible) {
    return null;
  }

  const peers = debugPanelStore.peers;

  const truncateMessage = (message: string | null): string => {
    if (!message) return 'N/A';

    const maxLength = appConfig.debugMessageMaxLength;
    if (message.length <= maxLength) {
      return message;
    }

    return message.substring(0, maxLength) + '...';
  };

  const formatTimestamp = (date: Date | null): string => {
    if (!date) return 'N/A';

    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatMessage = (content: string | null): string => {
    if (!content) return 'N/A';

    try {
      // Try to parse and pretty-print JSON
      const parsed = JSON.parse(content);
      return JSON.stringify(parsed, null, 2);
    } catch {
      // Not JSON, return as-is
      return content;
    }
  };

  const peerRows: React.ReactNode[] = [];

  peers.forEach((peer, key) => {
    (
      peerRows.push(
        <div key={key} className="bg-[var(--bg-secondary)] p-4 rounded-lg mb-4 last:mb-0 border border-[var(--border-color)]">
          <div className="flex justify-between items-center mb-3 pb-2 border-b border-[var(--border-color)]">
            <span className="text-[var(--text-primary)] text-sm font-semibold font-mono">ID: {key}</span>
            {/* <span className="text-[var(--text-secondary)] text-sm">{peer.playerName}</span> */}
          </div>

          <div className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-[var(--text-secondary)] text-xs font-medium uppercase">Last Sent:</span>
              <span className="text-[var(--text-secondary)] text-xs font-mono">
                {formatTimestamp(peer.lastMessageSent?.timestamp || null)}
              </span>
            </div>
            <pre className="bg-[var(--bg-primary)] p-2 rounded text-[var(--text-primary)] text-xs overflow-x-auto font-mono whitespace-pre-wrap break-words max-h-[100px] overflow-y-auto border border-[var(--border-color)]">
              {truncateMessage(formatMessage(peer.lastMessageSent?.content || null))}
            </pre>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-[var(--text-secondary)] text-xs font-medium uppercase">Last Received:</span>
              <span className="text-[var(--text-secondary)] text-xs font-mono">
                {formatTimestamp(peer.lastMessageReceived?.timestamp || null)}
              </span>
            </div>
            <pre className="bg-[var(--bg-primary)] p-2 rounded text-[var(--text-primary)] text-xs overflow-x-auto font-mono whitespace-pre-wrap break-words max-h-[100px] overflow-y-auto border border-[var(--border-color)]">
              {truncateMessage(formatMessage(peer.lastMessageReceived?.content || null))}
            </pre>
          </div>
        </div>
      )
    )
});

return (
  <div className="fixed bottom-24 right-5 w-96 max-w-[calc(100vw-40px)] max-h-[70vh] bg-[var(--bg-primary)] border-2 border-indigo-600 rounded-lg shadow-2xl overflow-hidden z-[9999] flex flex-col">
    <div className="bg-indigo-600 text-white p-3 flex justify-between items-center">
      <h3 className="m-0 text-base font-semibold">Debug Panel - WebRTC Peer Connections</h3>
      <span className="bg-white/20 px-2 py-1 rounded text-xs font-medium">{peers.size} connection(s)</span>
    </div>

    <div className="flex-1 overflow-y-auto p-4">
      {peers.size === 0 ? (
        <div className="text-center text-[var(--text-secondary)] py-8 italic">
          No active peer connections
        </div>
      ) : (
        peerRows
      )}
    </div>
  </div>
);
});

export default DebugPanel;
